'use client';

import { ENGLISH_IQ_EVENTS } from '@repo/analytics';
import {
  appleAppStoreUrl,
  buildEnglishIqResultWebUrl,
  buildEnglishIqWebUrl,
  googlePlayUrl,
} from '@repo/config';
import type { IEnglishIqResultV2 } from '@repo/sdk';
import { recordInstallIntent, trackEvent } from '@repo/web-chrome/lib/analytics';
import { QRCodeSVG } from 'qrcode.react';
import { useCallback, useEffect, useState } from 'react';
import { AppStoreBadgeRow } from '@/components/sections/app-store-badge-row';
import type { EnglishIqMessagesType } from '../../english-iq-messages-type';
import { prepareEnglishIqV2OwnerHandoff } from '../actions';
import { type EnglishIqV2DeviceType, resolveEnglishIqV2Device } from '../helper';
import { buildGooglePlayHandoffUrl } from './helper';

interface IEnglishIqV2DeviceCtaProps {
  messages: EnglishIqMessagesType['v2']['result'];
  owned: boolean;
  result: IEnglishIqResultV2;
}

export function EnglishIqV2DeviceCta({
  messages,
  owned,
  result,
}: IEnglishIqV2DeviceCtaProps): React.JSX.Element {
  const { device, resolved } = useEnglishIqV2ResolvedDevice();
  const handoff = useEnglishIqV2DesktopHandoff({
    device,
    owned,
    resolved,
    shareId: result.shareId,
  });
  const qrUrl = result.shareId
    ? buildEnglishIqResultWebUrl(result.shareId)
    : buildEnglishIqWebUrl();
  const openStore = async (): Promise<void> => {
    const href = await resolveInstallHref(device, owned, result.shareId);
    trackEvent(ENGLISH_IQ_EVENTS.storeOpen, { device, surface: 'web_v2' });
    recordInstallIntent(device === 'desktop' ? 'web' : device);
    window.location.assign(href);
  };
  return (
    <section className="mt-10 rounded-3xl border border-[#ff7a1a]/20 bg-[#ff7a1a]/8 p-6 sm:p-8">
      <h3 className="text-2xl font-semibold tracking-tight">{messages.deviceTitle}</h3>
      {device === 'desktop' ? (
        <DesktopDeviceCta handoff={handoff} messages={messages} qrUrl={qrUrl} />
      ) : (
        <MobileDeviceCta device={device} messages={messages} onOpen={openStore} />
      )}
    </section>
  );
}

function DesktopDeviceCta({
  handoff,
  messages,
  qrUrl,
}: {
  handoff: { retry: () => void; status: HandoffStatusType };
  messages: EnglishIqMessagesType['v2']['result'];
  qrUrl: string;
}): React.JSX.Element {
  if (handoff.status !== 'ready') {
    return (
      <div aria-busy={handoff.status === 'preparing'} className="mt-5" role="status">
        <p className="text-[#f7edda]/70">
          {handoff.status === 'failed' ? messages.handoffError : messages.handoffPreparing}
        </p>
        {handoff.status === 'failed' ? (
          <button
            className="mt-4 min-h-11 font-bold text-[#ffb36b] underline-offset-4 hover:underline"
            onClick={handoff.retry}
            type="button"
          >
            {messages.handoffRetry}
          </button>
        ) : null}
      </div>
    );
  }
  return (
    <div className="mt-5 flex flex-col gap-6 sm:flex-row sm:items-center">
      <div className="rounded-2xl bg-[#fff8e9] p-3 text-[#160c06]">
        <QRCodeSVG level="M" size={144} title={messages.qrAlt} value={qrUrl} />
      </div>
      <div>
        <p className="max-w-md text-[#f7edda]/70">{messages.desktopBody}</p>
        <AppStoreBadgeRow className="mt-5" />
      </div>
    </div>
  );
}

function MobileDeviceCta({
  device,
  messages,
  onOpen,
}: {
  device: Exclude<EnglishIqV2DeviceType, 'desktop'>;
  messages: EnglishIqMessagesType['v2']['result'];
  onOpen: () => Promise<void>;
}): React.JSX.Element {
  return (
    <button
      className="mt-5 min-h-14 rounded-full bg-[#fff8e9] px-7 font-extrabold text-[#160c06]"
      onClick={() => void onOpen()}
      type="button"
    >
      {device === 'ios' ? messages.iosCta : messages.androidCta}
    </button>
  );
}

function useEnglishIqV2ResolvedDevice(): {
  device: EnglishIqV2DeviceType;
  resolved: boolean;
} {
  const [device, setDevice] = useState<EnglishIqV2DeviceType>('desktop');
  const [resolved, setResolved] = useState(false);
  useEffect(() => {
    setDevice(resolveEnglishIqV2Device(navigator.userAgent));
    setResolved(true);
  }, []);
  return { device, resolved };
}

type HandoffStatusType = 'failed' | 'idle' | 'preparing' | 'ready';

function useEnglishIqV2DesktopHandoff({
  device,
  owned,
  resolved,
  shareId,
}: {
  device: EnglishIqV2DeviceType;
  owned: boolean;
  resolved: boolean;
  shareId: string | null;
}): { retry: () => void; status: HandoffStatusType } {
  const requiresHandoff = owned && Boolean(shareId);
  const [attempt, setAttempt] = useState(0);
  const [status, setStatus] = useState<HandoffStatusType>(requiresHandoff ? 'idle' : 'ready');
  useEffect(() => {
    if (!resolved || device !== 'desktop' || !requiresHandoff || !shareId) {
      return;
    }
    let active = true;
    const request = { attempt, shareId };
    setStatus('preparing');
    prepareEnglishIqV2OwnerHandoff(request.shareId)
      .then(() => active && setStatus('ready'))
      .catch(() => active && setStatus('failed'));
    return () => {
      active = false;
    };
  }, [attempt, device, requiresHandoff, resolved, shareId]);
  const retry = useCallback(() => setAttempt((value) => value + 1), []);
  return { retry, status };
}

async function resolveInstallHref(
  device: EnglishIqV2DeviceType,
  owned: boolean,
  shareId: string | null,
): Promise<string> {
  const fallback = device === 'ios' ? appleAppStoreUrl : googlePlayUrl;
  if (!owned || !shareId) {
    return fallback;
  }
  try {
    const token = await prepareEnglishIqV2OwnerHandoff(shareId);
    return device === 'android' ? buildGooglePlayHandoffUrl(token) : fallback;
  } catch {
    return fallback;
  }
}
